import { define } from 'acast-www-utils';
import { getState, getDefaultState, getComputedState, setState } from './state';
import { getDescriptor, getAllDescriptors } from './property-descriptor';

export default class Schema {
  static get Identity() {
    return new this();
  }

  static from(otherSchema) {
    if (otherSchema instanceof Schema === false) {
      throw new Error('The object supplied is not an instance of Schema');
    }
    return new this(otherSchema.getState());
  }

  constructor(values = {}) {
    setState(this, Object.assign(getDefaultState(this.constructor), values));
  }

  toJS() {
    return getComputedState(this);
  }

  getState() {
    const state = JSON.parse(JSON.stringify(getState(this)));
    const descriptors = getAllDescriptors(this.constructor);

    Object.keys(descriptors).forEach(name => {
      const { path } = descriptors[name];
      const isPartialPath = path.substring(0, name.length) === name;
      // There is an edge case here. If the given descriptor name
      // matches the path partially, we will delete the entire path
      // which will cause a loss of data. For example, on a marker
      // we have a property called `.thumbnail` that maps onto
      // `.thumbnail.original`. If we delete the `thumbnail` key here
      // we will also delete the entire sub-object. We need to check
      // that the path does not start with the name here and skip
      if (!isPartialPath) {
        delete state[name];
      }
    });

    return state;
  }

  valueOf() {
    return this.toJS();
  }

  toString() {
    return JSON.stringify(this.toJS());
  }

  merge(delta) {
    const state = this.getState();

    Object.keys(delta).forEach(key => {
      const descriptor = getDescriptor(this.constructor, key);
      const value = delta[key];

      if (descriptor === null || descriptor === undefined) {
        throw new Error(`Property ${key} is not part of ${this.constructor.name} schema`);
      }

      if (descriptor.mutable !== true) {
        throw new Error(`Cannot set immutable property ${key}`);
      }

      const isNull = value === null;
      const isNullAllowed = descriptor.allowNull === true;
      const isDefaultNull = descriptor.default === null;
      const isNullValid = isNull && (isNullAllowed || isDefaultNull);
      const isValid = descriptor.validate(value) || isNullValid;

      if (!isValid) {
        // eslint-disable-next-line max-len
        throw new Error(
          `Validation failed for property ${key}, value: ${value} of type ${typeof value}, expecting ${typeof descriptor.default}`
        );
      } else {
        define(state, descriptor.path, value);
      }
    });

    return new this.constructor(state);
  }
}
